import * as React from 'react';
import { cn } from '../../lib/utils';

export interface RailSection {
  id: string;
  label: string;
}

/**
 * A contents list for one long page, drawn as one.
 *
 * The storefront rail was already a list of anchors into a single scrolling
 * page, but it looked exactly like the settings rail beside it, which is real
 * navigation — and it marked nothing as current. So it read as a tab strip
 * whose tabs did not work: you clicked "Banner", every field was still there,
 * and nothing said you had arrived.
 *
 * Marking the section you are in is what tells the two apart. The rule down
 * the left is the design's, and it is the whole difference between "these are
 * pages" and "these are places in this page".
 */
export const SectionRail: React.FC<{
  sections: RailSection[];
  /** Rendered under the contents — the design puts one action there. */
  children?: React.ReactNode;
  /** For stories: pin the current section instead of watching the scroll. */
  activeId?: string;
}> = ({ sections, children, activeId }) => {
  const [current, setCurrent] = React.useState(activeId ?? sections[0]?.id);

  React.useEffect(() => {
    if (activeId) {
      setCurrent(activeId);
      return;
    }
    const targets = sections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => !!element);
    if (!targets.length) return;

    // The heading nearest the top of the viewport wins, which is what a reader
    // means by "the section I am in" — not merely whichever one is visible.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setCurrent(visible.target.id);
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, [sections, activeId]);

  return (
    <nav aria-label="Sections" className="text-sm">
      {sections.map((section) => {
        const active = section.id === current;
        return (
          <a
            key={section.id}
            href={`#${section.id}`}
            aria-current={active ? 'true' : undefined}
            data-testid={`section-link-${section.id}`}
            className={cn(
              'block border-l-2 py-1.5 pl-3 transition-colors',
              active
                ? 'border-primary font-semibold text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            {section.label}
          </a>
        );
      })}
      {children && <div className="mt-4">{children}</div>}
    </nav>
  );
};

/**
 * One section of a long settings page: what it is and why, in a gutter to the
 * left of the fields it governs.
 *
 * Without it every section was a card with a title, and a page of eight such
 * cards is a wall — nothing says which of them answers the question you came
 * with. The design puts a heading and one line of explanation beside the
 * fields, so the page can be skimmed down the left.
 */
export const GutterSection: React.FC<{
  id: string;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ id, title, description, children }) => (
  <section
    id={id}
    className="grid gap-x-8 gap-y-4 border-t border-border py-7 first:border-t-0 first:pt-1 md:grid-cols-[13rem_1fr]"
  >
    <div>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
    </div>
    <div>{children}</div>
  </section>
);
